/// <amd-module name="VSS/Features/ReduxDirectory/Redux/DirectoryActions" />
import { IDirectoryArtifact, ExceptionInfo } from "VSS/Features/Directory/DirectoryContracts";
import { ActionsUnion, createAction, trackedAction } from "redux/Utils/ActionHelper";
import { DirectoryTelemetryConstants } from "../Constants";
import { IUnifiedDirectoryDataProviderData, IDirectoryDataProviderData } from "../Contracts";

export const enum DirectoryActionTypes {
    ReceiveInitialData = "Directory/ReceiveInitialData",
    InitialDataLoaded = "Directory/InitialDataLoaded",
    InitialDataLoadFailed = "Directory/InitialDataLoadFailed",
    BeginLoadAllData = "Directory/BeginLoadAllData",
    RequestAllData = "Directory/RequestAllData",
    ReceiveAllData = "Directory/ReceiveAllData",
    LoadAllDataSucceeded = "Directory/LoadAllDataSucceeded",
    LoadAllDataFailed = "Directory/LoadAllDataFailed",
    ArtifactUpdated = "Directory/ArtifactUpdated",
    RemoveArtifact = "Directory/RemoveArtifact",
    NavigateToUrl = "Directory/NavigateToUrl",
    RowGroupCollapsedInitialized = "Directory/RowGroupCollapsedInitialized",
    RowGroupToggled = "Directory/RowGroupToggled",
    ToggleFilter = "Directory/ToggleFilter"
}

export const DirectoryActions = {
    /**
     * Action to indicate an exception was found in the data provider
     * @param exceptionInfo The exception returned from the data provider
     */
    receiveInitialData: <T extends IDirectoryArtifact>(directoryData: IUnifiedDirectoryDataProviderData<T>) =>
        createAction(DirectoryActionTypes.ReceiveInitialData, directoryData),

    /**
     * Action to populate the initial directory data from the data providers
     * @param directoryData The initial data
     */
    initialDataLoaded: <T extends IDirectoryArtifact>(directoryData: IUnifiedDirectoryDataProviderData<T>) =>
        trackedAction(createAction(DirectoryActionTypes.InitialDataLoaded, directoryData), {
            [DirectoryTelemetryConstants.OWNERS_COUNT]: directoryData.artifacts.length,
            [DirectoryTelemetryConstants.FAVORITE_COUNT]: directoryData.favorites ? directoryData.favorites.length : 0
        }),

    /**
     * Action to indicate an exception was found in the data provider
     * @param exceptionInfo The exception returned from the data provider
     */
    initialDataLoadFailed: (exceptionInfo: ExceptionInfo) => createAction(DirectoryActionTypes.InitialDataLoadFailed, exceptionInfo),

    /** Action to indicate we are starting to load the all group */
    beginLoadAllData: () => createAction(DirectoryActionTypes.BeginLoadAllData),

    /** Action to indicate we need the consumer of this code to deliver us the data we need for the all grouping. */
    requestAllData: () => createAction(DirectoryActionTypes.RequestAllData),

    /**
     * Action to indicate an exception was found in the data provider
     * @param exceptionInfo The exception returned from the data provider
     */
    receiveAllData: <T extends IDirectoryArtifact>(directoryData: IDirectoryDataProviderData<T>) =>
        createAction(DirectoryActionTypes.ReceiveAllData, directoryData),

    /**
     * Action to populate the all group
     * @param allDirectoryData The all group data returned from the data provider
     */
    loadAllDataSucceeded: <T extends IDirectoryArtifact>(allDirectoryData: IDirectoryDataProviderData<T>) =>
        trackedAction(createAction(DirectoryActionTypes.LoadAllDataSucceeded, allDirectoryData), {
            [DirectoryTelemetryConstants.OWNERS_COUNT]: allDirectoryData.artifacts.length
        }),

    /**
     * Action to indicate loading of the all group has failed
     * @param exceptionInfo Reason why the all group failed
     */
    loadAllDataFailed: (exceptionInfo: ExceptionInfo) => createAction(DirectoryActionTypes.LoadAllDataFailed, exceptionInfo),

    /**
     * Action to indicate that a directory artifact has been updated.
     * @param artifactId The id of the artifact
     * @param artifact The updated artifact
     */
    artifactUpdated: <T extends IDirectoryArtifact>(artifactId: string, artifact: T) =>
        createAction(DirectoryActionTypes.ArtifactUpdated, { artifactId, artifact }),

    /**
     * Action to indicate that an artifact should be removed from the directory.
     * @param artifactId The id of the artifact
     */
    removeArtifact: (artifactId: string) => createAction(DirectoryActionTypes.RemoveArtifact, { artifactId }),

    /**
     * Action which performs a FPS to another URL
     * @param url The new url
     */
    navigateToUrl: (url: string) => createAction(DirectoryActionTypes.NavigateToUrl, url),

    /** Action which populates the row group collapsed state */
    rowGroupCollapsedInitialized: (mapping: { [key: string]: boolean }) => createAction(DirectoryActionTypes.RowGroupCollapsedInitialized, mapping),

    /**
     * Action which expands/collapses a row group
     * @param rowGroupKey The row group key to expand/collapse
     */
    rowGroupToggled: (rowGroupKey: string, collapsed: boolean) => createAction(DirectoryActionTypes.RowGroupToggled, { rowGroupKey, collapsed }),

    /** Action which toggles the visibility of the filter */
    toggleFilter: () => createAction(DirectoryActionTypes.ToggleFilter)
};

export type DirectoryActions = ActionsUnion<typeof DirectoryActions>;
